import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Tag,
  Heart,
  Share2,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import styles from "./BlogPost.module.scss";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import type { BlogPost as BlogPostType } from "../../utils/data/blogPosts";
import { PageRoutes } from "../../utils/pageRoutes";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useGeoLocation from "../../components/geoLocation/GeoLoaction";
import {
  cmsPublicContent,
} from "../../utils/cmscontent/cms";
import { toastCommonestyle } from "../../utils/Toast";
import parser from "html-react-parser";
import { ShimmerLoader, ShimmerLoaderOneLine } from "../../utils/loader/ShimmerLoader";
import { Link, Element } from "react-scroll";
import  vidoes from "../../utils/Ticca.mp4"
import { resolveInsightPostRouteSegment, INSIGHT_POST_IDS, POST_CATEGORIES } from "../../utils/insights";
import { toggleBookmark, getBookmarkedIds } from "../../utils/bookmarks";
import PostCard from "../PostCard/PostCard";
const formatNumber = (num: number) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};


const emptyBlogPost: BlogPostType = {
  id: 0,
  title: "",
  description: "",
  author: "",
  authorRole: "",
  authorImage: "",
  publishDate: "",
  readingTime: 0,
  views: 0,
  likes: 0,
  shares: 0,
  category: "",
  tags: [],
  imageUrl: "",
  trending: false,
  content: {
    sections: [],
  },
};

function isKnownFallbackImageUrl(url: string): boolean {
  const n = (url || "").toLowerCase();
  return (
    n.includes("/404") ||
    n.includes("404.png") ||
    n.includes("notfound") ||
    n.includes("not-found") ||
    n.includes("oops")
  );
}

function buildTitleImageUrl(postId: string, title: string): string {
  const normalized = title.trim().split(" ").join("");
  if (!normalized) return "";
  return `https://public.ticca.com/images/png/insight_${postId}_${normalized}.png`;
}

function loadMissingImageFingerprint(): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const probe = new window.Image();
    probe.onload = () => {
      if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        resolve({ w: probe.naturalWidth, h: probe.naturalHeight });
      } else {
        resolve(null);
      }
    };
    probe.onerror = () => resolve(null);
    probe.src = `https://public.ticca.com/images/png/__probe_missing_blogpost__.png?t=${Date.now()}`;
  });
}

function validateImageCandidate(
  url: string,
  fallbackSize: { w: number; h: number } | null,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const loadedUrl = (img.currentSrc || img.src || "").toLowerCase();
      const isFallbackBySize =
        !!fallbackSize &&
        img.naturalWidth === fallbackSize.w &&
        img.naturalHeight === fallbackSize.h;
      resolve(
        !isKnownFallbackImageUrl(loadedUrl) &&
          !isKnownFallbackImageUrl(url) &&
          !isFallbackBySize,
      );
    };
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function extractImageCandidatesFromHtmlBlocks(htmlArray: string[]): string[] {
  const candidates: string[] = [];

  for (const block of htmlArray) {
    if (!block || !block.trim()) continue;
    const parser = new DOMParser();
    const doc = parser.parseFromString(block, "text/html");
    const mediaEls = Array.from(doc.querySelectorAll("img, source"));

    mediaEls.forEach((media) => {
      const srcsetFirst = media
        .getAttribute("srcset")
        ?.split(",")?.[0]
        ?.trim()
        ?.split(" ")?.[0];

      const src =
        media.getAttribute("src")?.trim() ||
        srcsetFirst ||
        media.getAttribute("data-src")?.trim() ||
        media.getAttribute("data-lazy-src")?.trim() ||
        media.getAttribute("data-original")?.trim() ||
        "";

      if (!src) return;
      try {
        candidates.push(new URL(src, "https://public.ticca.com").toString());
      } catch {
        // Ignore malformed image URLs from CMS.
      }
    });
  }

  return Array.from(new Set(candidates.filter(Boolean)));
}

function extractRenderableHtmlBlocks(doc: Document): string[] {
  const blocks: string[] = [];

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const html = (node as HTMLElement).outerHTML?.trim();
      if (html) {
        blocks.push(html);
      }
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").trim();
      if (text) {
        blocks.push(text);
      }
    }
  });

  return blocks;
}

function htmlToPlainText(html: string): string {
  if (!html) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
}

const BlogPosts = () => {
  const navigation = useNavigate();
  const routeLocation = useLocation();
  const geoLocation = useGeoLocation();
  // Extract the active category from navigation state so all nav calls preserve it
  const activeCategory = (routeLocation.state as { selectedCategory?: string } | null)?.selectedCategory || "";
  const [htmlString, setHtmlStr] = useState<BlogPostType>(emptyBlogPost);

  const { slug } = useParams();
  const [featuredImageCandidates, setFeaturedImageCandidates] = useState<string[]>([]);
  const [featuredImageCandidateIndex, setFeaturedImageCandidateIndex] = useState(0);
  const fallbackImageSizeRef = useRef<{ w: number; h: number } | null>(null);
  const resolvedImageUrl =
    featuredImageCandidates[featuredImageCandidateIndex] || htmlString.imageUrl;
  
  const [likes, setLikes] = useState(0);
  const [htmlStrRemoved, setHtmlStrRemoved] = useState<string[]>([]);
  const [featuredImageError, setFeaturedImageError] = useState(false);
  const [isLoadingPost, setIsLoadingPost] = useState(true);
  const [hasResolvedPostFetch, setHasResolvedPostFetch] = useState(false);
  const [postCategory, setPostCategory] = useState("");
  const [showBookmarkNotice, setShowBookmarkNotice] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState<{ id: number; slug: string; title: string; description?: string; category?: string }[]>([]);
  const [adjacentPosts, setAdjacentPosts] = useState<{ prev: { id: number; slug: string; title: string } | null; next: { id: number; slug: string; title: string } | null }>({ prev: null, next: null });
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "TICCA | Insights";
  }, []);
  function wpArrayToBlogPost(htmlArray: string[]): BlogPostType {
    // 1. Parse sections
    const sections: { heading?: string; paragraphs: string[] }[] = [];

    let extractedTitle = "";
    let extractedDescription = "";
    let extractedCategory = "";
    let extractedTags: string[] = [];
    const extractedImageCandidates = extractImageCandidatesFromHtmlBlocks(htmlArray);

    htmlArray.forEach((block, index) => {
      if (!block || !block.trim()) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(block, "text/html");

      const h2 = doc.querySelector("h2");
      const h3 = doc.querySelector("h3");
      const h1 = doc.querySelector("h1");


      const heading =
        h1?.textContent?.trim() || h2?.textContent?.trim() || h3?.textContent?.trim() || undefined;

      if (index === 0 && heading) {
        extractedTitle = heading;
      }

      // Remove headings so body rendering does not duplicate section titles.
      if (h1) h1.remove();
      if (h2) h2.remove();
      if (h3) h3.remove();

      const paragraphs: string[] = [];

      doc.querySelectorAll("p").forEach((p) => {
        const txt = p.textContent?.trim();
        if (!txt) return;

        // Extract description from first section first paragraph
        if (!extractedDescription && index === 0) {
          extractedDescription = txt;
        }

        // Extract category
        if (txt.startsWith("Category:")) {
          extractedCategory = txt.replace("Category:", "").trim();
          p.remove();
          return;
        }

        // Extract tags
        if (txt.startsWith("Tags:")) {
          extractedTags = txt
            .replace("Tags:", "")
            .split(",")
            .map((t) => t.trim());
          p.remove();
          return;
        }
      });

      paragraphs.push(...extractRenderableHtmlBlocks(doc));

      if (heading || paragraphs.length) {
        sections.push({ heading, paragraphs });
      }
    });

    // 2. Stable ID
    const stableId = (input: string): number => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        hash = (hash << 5) - hash + input.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash);
    };

    const id = stableId(extractedTitle || "post");

    // 3. Build final BlogPost
    const post: BlogPostType = {
      id,
      title: extractedTitle || "Untitled",
      description: extractedDescription || "",
      author: "cross-ston",
      authorRole: "admin",
      authorImage:
        `https://public.ticca.com/images/png/${extractedTitle.trim().split(" ").join("")}.png`,
      publishDate: "23-05-2025",
      readingTime: Math.max(
        1,
        Math.ceil(
          htmlToPlainText(
            sections
              .flatMap((s) => s.paragraphs)
              .join(" "),
          )
            .split(/\s+/)
            .filter(Boolean).length / 200,
        ),
      ),
      views: 2324,
      likes: 1243,
      shares: 56,
      category: extractedCategory || "",
      tags: extractedTags,
      imageUrl: extractedImageCandidates[0] || "",
      trending: true,

      content: {
        sections,
      },
    };

    return post;
  }

  function extractTitleAndDescription(htmlString: string) {
    if (!htmlString || typeof htmlString !== "string") {
      return { title: "", description: "" };
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // 1. Get first heading as title
    const firstHeading = doc.querySelector("h1, h2, h3");

    let title = "";
    if (firstHeading) {
      title = firstHeading.textContent?.trim() || "";
    }

    // 2. Remove all headings from document
    doc.querySelectorAll("h1, h2, h3").forEach((h) => h.remove());

    // 3. Keep ONLY <p> tags
    const paragraphs = Array.from(doc.querySelectorAll("p"));

    const description = paragraphs.map((p) => p.outerHTML).join("");

    return {
      title,
      description,
    };
  }

  const fetchData = async (postId: string) => {
    try {
      setIsLoadingPost(true);
      setHasResolvedPostFetch(false);
      setFeaturedImageError(false);
      setFeaturedImageCandidateIndex(0);

      if (!fallbackImageSizeRef.current) {
        fallbackImageSizeRef.current = await loadMissingImageFingerprint();
      }

      const finaldata = await cmsPublicContent({
        post_id: postId,
        lat: geoLocation.coordinates.lat,
        log: geoLocation.coordinates.lng,
      });
      const { title, description } = extractTitleAndDescription(finaldata[0]);
      const titleImageCandidate = buildTitleImageUrl(postId, title || "");

      const extractedCandidates = extractImageCandidatesFromHtmlBlocks(finaldata).filter(
        (url) => !isKnownFallbackImageUrl(url),
      );

      const validTitleCandidate =
        titleImageCandidate &&
        (await validateImageCandidate(
          titleImageCandidate,
          fallbackImageSizeRef.current,
        ))
          ? titleImageCandidate
          : "";

      const imageCandidates = Array.from(
        new Set([
          ...extractedCandidates,
          validTitleCandidate,
        ].filter(Boolean)),
      );
      setFeaturedImageCandidates(imageCandidates);

      const post = wpArrayToBlogPost(finaldata);
      const numericId = parseInt(postId, 10);
      setHtmlStr({
        ...post,
        // Match the tile view trending logic (id % 5 === 0)
        trending: Number.isFinite(numericId) ? numericId % 5 === 0 : false,
        imageUrl: post.imageUrl || imageCandidates[0] || "",
      });

      // Resolve category: POST_CATEGORIES first, then CMS-extracted, fallback "General"
      const resolvedCat = (Number.isFinite(numericId) && POST_CATEGORIES[numericId])
        || post.category
        || "General";
      setPostCategory(resolvedCat);
      setIsBookmarked(getBookmarkedIds().has(numericId));

      setHtmlStrRemoved([title, description]);
    } catch (error) {
      toast.error("Something went wrong", toastCommonestyle as any);
    } finally {
      setIsLoadingPost(false);
      setHasResolvedPostFetch(true);
    }
  };
  useEffect(() => {
    if (!slug || !geoLocation.coordinates.lat || !geoLocation.coordinates.lng) {
      return;
    }

    let cancelled = false;

    const resolveAndFetchPost = async () => {
      const resolvedPost = await resolveInsightPostRouteSegment(
        slug,
        geoLocation.coordinates.lat,
        geoLocation.coordinates.lng,
      );

      if (cancelled) {
        return;
      }

      if (!resolvedPost) {
        setIsLoadingPost(false);
        setHasResolvedPostFetch(true);
        toast.error("Insight article not found", toastCommonestyle as any);
        navigation(PageRoutes.insights);
        return;
      }

      if (slug !== resolvedPost.slug) {
        navigation(`${PageRoutes.insights}/${resolvedPost.slug}`, {
          replace: true,
          state: routeLocation.state,
        });
      }

      await fetchData(String(resolvedPost.id));

      // Adjacent posts: scoped to same category (or full list if "All")
      const navState = routeLocation.state as { selectedCategory?: string } | null;
      const activeCategory = navState?.selectedCategory || "All";
      const categoryFilteredIds = activeCategory === "All"
        ? INSIGHT_POST_IDS
        : INSIGHT_POST_IDS.filter((id) => POST_CATEGORIES[id] === activeCategory);

      const currentIndex = categoryFilteredIds.indexOf(resolvedPost.id);

      // Fetch adjacent post metadata asynchronously (shared by prev/next + related)
      const fetchAdjacentMeta = async (id: number) => {
        try {
          const res = await cmsPublicContent({
            post_id: String(id),
            lat: String(geoLocation.coordinates.lat),
            log: String(geoLocation.coordinates.lng),
          });
          const html = res.join(" ");
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const titleEl = doc.querySelector("h1, h2, h3");
          const title = titleEl?.textContent?.trim() || "";
          const slug = title.toLowerCase().trim().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `post-${id}`;
          // Extract description (first <p> that isn't category/tags)
          let description = "";
          const firstP = Array.from(doc.querySelectorAll("p")).find(
            (p) => !p.textContent?.trim().toLowerCase().startsWith("category:") &&
                   !p.textContent?.trim().toLowerCase().startsWith("tags:"),
          );
          if (firstP) {
            description = firstP.textContent?.trim() || "";
          }
          return { id, slug, title: title || `Post ${id}`, description, category: POST_CATEGORIES[id] || "General" };
        } catch {
          return null;
        }
      };

      if (currentIndex !== -1) {
        const prevId = currentIndex > 0 ? categoryFilteredIds[currentIndex - 1] : null;
        const nextId = currentIndex < categoryFilteredIds.length - 1 ? categoryFilteredIds[currentIndex + 1] : null;
        if (prevId || nextId) {
          const [prev, next] = await Promise.all([
            prevId ? fetchAdjacentMeta(prevId) : Promise.resolve(null),
            nextId ? fetchAdjacentMeta(nextId) : Promise.resolve(null),
          ]);
          if (!cancelled) setAdjacentPosts({ prev, next });
        }
      }

      // Related posts: same category as current post (fills the screen),
      // then backfilled from other categories when the category is sparse.
      const relatedSectionRef = document.getElementById("related-posts-section");
      const sectionWidth = relatedSectionRef?.clientWidth || window.innerWidth;
      // Small tiles (min ~200px each) so the row fills without being oversized
      const relatedCount = Math.max(1, Math.floor(sectionWidth / 200));

      const relatedCategory = POST_CATEGORIES[resolvedPost.id];
      const sameCategoryIds = relatedCategory
        ? INSIGHT_POST_IDS.filter((id) => POST_CATEGORIES[id] === relatedCategory && id !== resolvedPost.id)
        : [];
      // Backfill from other categories to fill the row
      const otherIds = INSIGHT_POST_IDS.filter((id) => id !== resolvedPost.id && !sameCategoryIds.includes(id));
      const relatedIds = [...sameCategoryIds, ...otherIds].slice(0, relatedCount);

      if (relatedIds.length > 0) {
        const relatedResults = await Promise.allSettled(relatedIds.map((id) => fetchAdjacentMeta(id)));
        if (!cancelled) {
          const related = relatedResults
            .map((r) => (r.status === "fulfilled" ? r.value : null))
            .filter(Boolean) as { id: number; slug: string; title: string; description?: string; category?: string }[];
          setRelatedPosts(related);
        }
      }
    };

    resolveAndFetchPost();

    return () => {
      cancelled = true;
    };
  }, [
    slug,
    geoLocation.coordinates.lat,
    geoLocation.coordinates.lng,
    navigation,
    routeLocation.state,
  ]);

  // Keyboard navigation (ArrowLeft / ArrowRight)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const navState = activeCategory ? { selectedCategory: activeCategory } : undefined;
      if (e.key === "ArrowLeft" && adjacentPosts.prev) {
        navigation(`${PageRoutes.insights}/${adjacentPosts.prev.slug}`, { state: navState });
      } else if (e.key === "ArrowRight" && adjacentPosts.next) {
        navigation(`${PageRoutes.insights}/${adjacentPosts.next.slug}`, { state: navState });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [adjacentPosts, navigation, activeCategory]);

  // Swipe navigation (mobile): swipe left → next post, swipe right → prev post
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;

    // Only trigger horizontal swipe if it's clearly horizontal (avoid scroll conflicts)
    if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    const navState = activeCategory ? { selectedCategory: activeCategory } : undefined;
    if (deltaX < 0 && adjacentPosts.next) {
      // Swipe left → next post
      navigation(`${PageRoutes.insights}/${adjacentPosts.next.slug}`, { state: navState });
    } else if (deltaX > 0 && adjacentPosts.prev) {
      // Swipe right → prev post
      navigation(`${PageRoutes.insights}/${adjacentPosts.prev.slug}`, { state: navState });
    }
  };

  // Auto-dismiss keyboard hint when user presses a key
  const [showKeyboardHint, setShowKeyboardHint] = useState(true);
  useEffect(() => {
    if (!showKeyboardHint) return;
    const timer = setTimeout(() => setShowKeyboardHint(false), 6000);
    const dismiss = () => setShowKeyboardHint(false);
    document.addEventListener("keydown", dismiss, { once: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", dismiss);
    };
  }, [showKeyboardHint]);

  return (
    <>
      {/* Keyboard shortcut hint */}
      <AnimatePresence>
        {showKeyboardHint && (adjacentPosts.prev || adjacentPosts.next) && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-md border border-gray-200 px-4 py-2 text-xs text-gray-500 shadow-lg"
          >
            <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 bg-gray-50 text-[10px] font-mono text-gray-600">←</kbd>
            <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 bg-gray-50 text-[10px] font-mono text-gray-600">→</kbd>
            <span>Navigate between posts</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* First-time bookmark notice */}
      <AnimatePresence>
        {showBookmarkNotice && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 rounded-full bg-gray-900/90 backdrop-blur-md border border-gray-700 px-5 py-2.5 text-xs text-white shadow-lg flex items-center gap-2"
          >
            <BookmarkCheck className="w-4 h-4 text-blue-400 shrink-0" />
            <span>Bookmark saved to your browser — no data sent to any server</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={styles["blog__wrapper"]}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Prev / Next floating nav */}
        {adjacentPosts.prev && (
          <div className={styles["blog__nav-prev"]}>
            <div className={styles["blog__nav-tooltip"]}>
              <motion.button
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                onClick={() => {
                  const navState = activeCategory ? { selectedCategory: activeCategory } : undefined;
                  navigation(`${PageRoutes.insights}/${adjacentPosts.prev!.slug}`, { state: navState });
                }}
                className={styles["blog__nav-btn"]}
              >
                <ArrowLeft className={styles["blog__nav-icon"]} />
              </motion.button>
              <span className={styles["blog__nav-tooltip-text"]}>{adjacentPosts.prev.title}</span>
            </div>
          </div>
        )}
        {adjacentPosts.next && (
          <div className={styles["blog__nav-next"]}>
            {/* Bookmark icon — bookmarks the NEXT post (matches nav direction) */}
            <button
              onClick={() => {
                const nid = adjacentPosts.next!.id;
                const added = toggleBookmark({ id: nid, title: adjacentPosts.next!.title, slug: adjacentPosts.next!.slug });
                if (added && !sessionStorage.getItem("bookmark_notice_seen")) {
                  sessionStorage.setItem("bookmark_notice_seen", "1");
                  setShowBookmarkNotice(true);
                  setTimeout(() => setShowBookmarkNotice(false), 3000);
                }
              }}
              className={styles["blog__bookmark-btn"]}
              title={
                getBookmarkedIds().has(adjacentPosts.next.id)
                  ? "Remove bookmark"
                  : "Bookmark this post"
              }
            >
              {getBookmarkedIds().has(adjacentPosts.next.id) ? (
                <BookmarkCheck className={styles["blog__bookmark-icon-filled"]} />
              ) : (
                <Bookmark className={styles["blog__nav-icon"]} />
              )}
            </button>
            <div className={styles["blog__nav-tooltip"]}>
              <motion.button
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.15 }}
                onClick={() => {
                  const navState = activeCategory ? { selectedCategory: activeCategory } : undefined;
                  navigation(`${PageRoutes.insights}/${adjacentPosts.next!.slug}`, { state: navState });
                }}
                className={styles["blog__nav-btn"]}
              >
                <ArrowRight className={styles["blog__nav-icon"]} />
              </motion.button>
              <span className={styles["blog__nav-tooltip-text"]}>{adjacentPosts.next.title}</span>
            </div>
          </div>
        )}

        {/* Header */}
        <header className={styles["blog__header"]}>
          <div className={styles["blog__header-inner"]}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className={styles["blog__badges"]}>
                <span className={styles["blog__category-badge"]}>
                  {postCategory || "General"}
                </span>

                {htmlString.trending && (
                  <span className={styles["blog__trending-badge"]}>
                    <TrendingUp className={styles["blog__trending-icon"]} />
                    Trending Now
                  </span>
                )}

                {/* Bookmark current post — right aligned */}
                <button
                  onClick={() => {
                    const added = toggleBookmark({
                      id: htmlString.id,
                      title: htmlString.title,
                      slug: slug || "",
                    });
                    setIsBookmarked(added);
                    if (added && !sessionStorage.getItem("bookmark_notice_seen")) {
                      sessionStorage.setItem("bookmark_notice_seen", "1");
                      setShowBookmarkNotice(true);
                      setTimeout(() => setShowBookmarkNotice(false), 3000);
                    }
                  }}
                  className={styles["blog__header-bookmark"]}
                  title={isBookmarked ? "Remove bookmark" : "Bookmark this post"}
                  aria-label={isBookmarked ? "Remove bookmark" : "Bookmark this post"}
                >
                  {isBookmarked ? (
                    <BookmarkCheck className={styles["blog__bookmark-icon-filled"]} />
                  ) : (
                    <Bookmark className={styles["blog__nav-icon"]} />
                  )}
                </button>
              </div>

              <h1 className={styles["blog__title"]}>
                {htmlStrRemoved[1] ?  htmlStrRemoved[0] : <ShimmerLoaderOneLine/>}
              </h1>
              <p className={styles["blog__subtitle"]}>
                {htmlStrRemoved[1] ? parser(htmlStrRemoved[1]) : <>
                  <ShimmerLoader/><br/>
                      <ShimmerLoader/><br/>
                </>}
              </p>
              <div className={styles["blog__author-row"]}>
                {/* <div className={styles["blog__author"]}>
                  <div className={styles["blog__author-avatar"]}>TT</div>
                  <div className={styles["blog__author-name"]}>TICCA Team</div>
                </div> */}

                {/* <div className={styles["blog__meta-row"]}>
                  <div className={styles["blog__meta-item"]}>
                    <Calendar className={styles["blog__meta-icon"]} />
                    <span>
                      {new Date("2025-4-10").toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>

                  <div className={styles["blog__meta-item"]}>
                    <Clock className={styles["blog__meta-icon"]} />
                    <span>{"100"} min read</span>
                  </div>

                  <div className={styles["blog__meta-item"]}>
                    <Eye className={styles["blog__meta-icon"]} />
                    <span>{formatNumber(1343)} views</span>
                  </div>
                </div> */}
              </div>
            </motion.div>
          </div>
        </header>

        {/* Main layout */}
        {htmlString.content.sections.length > 0 ? (
          <main className={styles["blog__main"]}>
            <div className={styles["blog__grid"]}>
              {/* Article column */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={styles["blog__article-col"]}
              >
                {/* Featured image */}
                <div className={styles["blog__featured-wrap"]}>
                  

                  {!resolvedImageUrl || featuredImageError ? (
                    <video
                      src={"https://public.ticca.com/video/BlogGeneralvideo.mp4"}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls={false}
                      className={styles["blog__featured-img"]}
                    />
                  ) : (
                    <img
                      src={resolvedImageUrl}
                      alt={htmlString.title}
                      className={styles["blog__featured-img"]}
                      onLoad={(event) => {
                        const el = event.currentTarget;
                        const loadedUrl = (el.currentSrc || el.src || "").toLowerCase();
                        const bad = fallbackImageSizeRef.current;
                        const isFallbackBySize =
                          !!bad &&
                          el.naturalWidth === bad.w &&
                          el.naturalHeight === bad.h;

                        if (isKnownFallbackImageUrl(loadedUrl) || isFallbackBySize) {
                          const nextIndex = featuredImageCandidateIndex + 1;
                          if (nextIndex < featuredImageCandidates.length) {
                            setFeaturedImageCandidateIndex(nextIndex);
                          } else {
                            setFeaturedImageError(true);
                          }
                        }
                      }}
                      onError={() => {
                        const nextIndex = featuredImageCandidateIndex + 1;
                        if (nextIndex < featuredImageCandidates.length) {
                          setFeaturedImageCandidateIndex(nextIndex);
                        } else {
                          setFeaturedImageError(true);
                        }
                      }}
                    />
                  )}
                </div>

                {/* Article body */}
                <div className={styles["blog__card"]}>
                  <div className={styles["blog__prose"]}>
                    {htmlString.content.sections.map((section, idx) => (
                      <Element
                        key={idx}
                        name={`insights-blog-post-${idx}`}
                        className={`insights-blog-post-${idx}`}
                      >
                      <section className={styles["blog__section"]}>
                        {section.heading && (
                          <h2 className={styles["blog__section-heading"]}>
                            <span
                              className={styles["blog__section-line"]}
                            ></span>
                            {section.heading}
                          </h2>
                        )}

                        {section.paragraphs.map((p, j) => (
                          <div key={j} className={styles["blog__paragraph"]}>
                            {parser(p)}
                          </div>
                        ))}
                      </section>
                      </Element>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className={styles["blog__card"]}>
                  <div className={styles["blog__tags-header"]}>
                    <Tag className={styles["blog__tags-icon"]} />
                    <h3 className={styles["blog__tags-title"]}>
                      Related Topics
                    </h3>
                  </div>

                  <div className={styles["blog__tags-list"]}>
                    {htmlString.tags.map((t) => (
                      <button key={t} className={styles["blog__tag-btn"]}>
                        #{t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className={styles["blog__cta-card"]}>
                  <div className={styles["blog__cta-inner"]}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className={styles["blog__cta-content"]}
                    >
                      <div className={styles["blog__cta-iconbox"]}>
                        <Sparkles className={styles["blog__cta-icon"]} />
                      </div>

                      <h2 className={styles["blog__cta-title"]}>
                        Build Confidence Through Adaptive Control
                      </h2>
                      <p className={styles["blog__cta-text"]}>
                        Experience the power of AI-driven project management and
                        collaboration tools designed for modern remote teams.
                      </p>

                      <div className={styles["blog__cta-actions"]}>
                        <button
                          onClick={() => {
                            let Api_URL: any = "";
                            const env = process.env.REACT_APP_STAGE_ENV;
                            switch (env) {
                              case "dev":
                                Api_URL = `https://dev.www.ticca.com`;
                                break;
                              case "stage":
                                Api_URL = `https:/stage.www.ticca.com`;
                                break;
                              case "prod":
                                Api_URL = `https://www.ticca.com`;
                                break;
                              default:
                                Api_URL = `https://dev.www.ticca.com`;
                            }
                            window.location.href = Api_URL;
                          }}
                          className={styles["blog__cta-primary"]}
                        >
                          <span>Sign Up to TICCA</span>
                          <ChevronRight className={styles["blog__cta-arrow"]} />
                        </button>

                        <span
                          // href="https://dev.www.ticca.ltd/"
                          // target="_blank"
                          // rel="noopener noreferrer"
                          onClick={() => {
                            navigation(PageRoutes.InvestorCenter);
                          }}
                          className={styles["blog__cta-secondary"]}
                        >
                          <span>Visit Company Site</span>
                          <ChevronRight className={styles["blog__cta-arrow"]} />
                        </span>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Author bio */}
                <div className={styles["blog__bio-card"]}>
                  <div className={styles["blog__bio-inner"]}>
                    <div className={styles["blog__bio-avatar"]}>TT</div>
                    <div className={styles["blog__bio-text"]}>
                      <h3 className={styles["blog__bio-title"]}>
                        Written by TICCA Team
                      </h3>
                      <p className={styles["blog__bio-desc"]}>
                        Our expert team specializes in AI technology and
                        cognitive systems, dedicated to exploring how artificial
                        intelligence can transform business operations and human
                        collaboration for modern remote teams.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.article>

              {/* Sidebar */}
              <motion.aside
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className={styles["blog__sidebar-col"]}
              >
                <div className={styles["blog__sidebar-sticky"]}>
                  {/* Table of contents */}
                  <div className={styles["blog__card"]}>
                    <h3 className={styles["blog__sidebar-title"]}>
                      <span className={styles["blog__sidebar-line"]}></span>In
                      This Article
                    </h3>

                    <nav className={styles["blog__toc"]}>
                      {htmlString.content.sections
                        .filter((s) => s.heading)
                        .map((s, i) => (
                            <Link to={`insights-blog-post-${i}`} smooth={true} duration={500} offset={-100}>
                          <a
                            key={i}
                            onClick={() => {}}
                            className={styles["blog__toc-item"]}
                          >
                            <ChevronRight
                              className={styles["blog__toc-icon"]}
                            />
                            <span className={styles["blog__toc-text"]}>
                              {s.heading}
                            </span>
                          </a>
                          </Link>
                        ))}
                    </nav>
                  </div>

                  {/* Quick stats */}
                  {/* <div className={styles["blog__card"]}>
                    <h3 className={styles["blog__sidebar-title"]}>
                      <span className={styles["blog__sidebar-line"]}></span>
                      Article Stats
                    </h3>

                    <div className={styles["blog__stats-list"]}>
                      <div className={styles["blog__stats-row"]}>
                        <div className={styles["blog__stats-left"]}>
                          <Eye className={styles["blog__stats-icon"]} />
                          <span className={styles["blog__stats-label"]}>
                            Views
                          </span>
                        </div>
                        <div className={styles["blog__stats-value"]}>
                          {formatNumber(htmlString.views)}
                        </div>
                      </div>

                      <div className={styles["blog__stats-row"]}>
                        <div className={styles["blog__stats-left"]}>
                          <Heart className={styles["blog__stats-icon"]} />
                          <span className={styles["blog__stats-label"]}>
                            Likes
                          </span>
                        </div>
                        <div className={styles["blog__stats-value"]}>
                          {formatNumber(likes)}
                        </div>
                      </div>

                      <div className={styles["blog__stats-row"]}>
                        <div className={styles["blog__stats-left"]}>
                          <Share2 className={styles["blog__stats-icon"]} />
                          <span className={styles["blog__stats-label"]}>
                            Shares
                          </span>
                        </div>
                        <div className={styles["blog__stats-value"]}>
                          {formatNumber(htmlString.shares ?? 0)}
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Newsletter */}
                  {/* <div className={styles["blog__card"]}>
                    <div className={styles["blog__newsletter"]}>
                      <div className={styles["blog__newsletter-iconbox"]}>
                        <Sparkles className={styles["blog__newsletter-icon"]} />
                      </div>

                      <h3 className={styles["blog__newsletter-title"]}>
                        Never Miss an Update
                      </h3>
                      <p className={styles["blog__newsletter-desc"]}>
                        Get the latest insights delivered to your inbox
                      </p>

                      <input
                        className={styles["blog__newsletter-input"]}
                        placeholder="Enter your email"
                      />
                      <button className={styles["blog__newsletter-btn"]}>
                        Subscribe
                      </button>
                    </div>
                  </div> */}

                  {/* Related posts
                {relatedPosts?.length > 0 && (
                  <div className={styles["blog__card"]}>
                    <div className={styles["blog__related-header"]}>
                      <Tag className={styles["blog__related-icon"]} />
                      <h3 className={styles["blog__related-title"]}>Related Articles</h3>
                    </div>

                    <div className={styles["blog__related-list"]}>
                      {relatedPosts.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => onNavigateToPost(p.id)}
                          className={styles["blog__related-item"]}
                        >
                          <img src={p.imageUrl} alt={p.title} className={styles["blog__related-img"]} />
                          <div className={styles["blog__related-body"]}>
                            <h4 className={styles["blog__related-name"]}>{p.title}</h4>
                            <div className={styles["blog__related-meta"]}>
                              <Clock className={styles["blog__related-clock"]} />
                              <span>{p.readingTime} min</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
                </div>
              </motion.aside>
            </div>

            {/* Related content sections */}
            <div className={styles["blog__related-sections"]}>
              {/* You may also want to read */}
              {relatedPosts.length > 0 && (
                <motion.section
                  id="related-posts-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className={styles["blog__related-block"]}
                >
                  <div className={styles["blog__section-head"]}>
                    <div className={styles["blog__section-iconbox"]}>
                      <Sparkles className={styles["blog__section-icon"]} />
                    </div>
                    <div>
                      <h2 className={styles["blog__section-title"]}>You may also want to read</h2>
                      <p className={styles["blog__section-sub"]}>More insights in this topic</p>
                    </div>
                  </div>

                  <div className={styles["blog__cards-grid"]}>
                    {relatedPosts.map((p, i) => (
                      <motion.article
                        key={p.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 + i * 0.08 }}
                        className="h-full cursor-pointer"
                      >
                        <PostCard
                          post={p}
                          showBookmark={false}
                          showCategory={true}
                          showDescription={true}
                          onClick={() => navigation(`${PageRoutes.insights}/${p.slug}`)}
                        />
                      </motion.article>
                    ))}
                  </div>
                </motion.section>
              )}
            </div>
          </main>
        ) : !isLoadingPost && hasResolvedPostFetch ? (
          <main className={styles["blog__main"]}>
            <div className={styles["blog__card"]}>
              <div className={styles["blog__prose"]}>
                <h2 className={styles["blog__section-heading"]}>No article available right now</h2>
              </div>
            </div>
          </main>
        ) : (
          <header className={styles["blog__header"]}>
            <div className={styles["page-shimmer"]}></div>
          </header>
        )}
      </div>
    </>
  );
};
export default BlogPosts;
